// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import axios, { AxiosInstance, AxiosError, type AxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { extractApiErrorMessage, extractRetryAfterSeconds } from './errorMessage';
import { buildQuotaExceededPayload, emitQuotaExceededEvent, isQuotaExceededError } from './quotaExceeded';
import type { QuotaExceededPayload } from './quotaExceeded';
import { API_DEFAULT_TIMEOUT_MS } from '@/config/runtimeConstants';

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const DEFAULT_TOAST_COOLDOWN_MS = 3000;
const RATE_LIMIT_TOAST_COOLDOWN_MS = 30000;
const lastToastAt = new Map<string, number>();
const PUBLIC_AUTH_ROUTES = new Set([
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/social-callback',
]);

// ADR-025 (revised) + H-5b: the gateway mirrors the JWT into an HttpOnly
// cookie (physics_agent_auth). The cookie is the XSS-resistant carrier the
// browser attaches automatically (client sends withCredentials); localStorage
// below is only the UI-state fallback for API clients without cookie jars.
const TOKEN_STORAGE_KEY = 'auth-token';
let _inMemoryAccessToken: string | null = null;

export function getAccessToken(): string | null {
  if (_inMemoryAccessToken) return _inMemoryAccessToken;
  // Fall back to localStorage on fresh page load (in-memory var is reset)
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token: string | null): void {
  _inMemoryAccessToken = token;
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable (e.g. private browsing quota exceeded) —
    // token remains in-memory only for the current session.
  }
}

/** One-time cleanup: remove legacy localStorage access_token if present. */
export function clearLegacyLocalStorageToken(): void {
  const hadLegacyToken = localStorage.getItem('access_token') !== null;
  localStorage.removeItem('access_token');
  _tokenCleanupEvidenceSink?.({ hadLegacyToken });
}

// ── Token cleanup usage evidence (deletion-gate mechanism) ──────
/** One-shot evidence emitted when clearLegacyLocalStorageToken executes. */
export type TokenCleanupEvidence = {
  /** Whether a legacy access_token key was actually present in localStorage. */
  hadLegacyToken: boolean;
};

let _tokenCleanupEvidenceSink: ((e: TokenCleanupEvidence) => void) | null = null;

/**
 * Install a one-shot evidence sink for deletion-gate decisions.
 * Call with null to remove. The sink is fired every time
 * `clearLegacyLocalStorageToken` executes.
 */
export function setTokenCleanupEvidenceSink(
  fn: ((e: TokenCleanupEvidence) => void) | null
): void {
  _tokenCleanupEvidenceSink = fn;
}

export interface ApiRequestConfig extends AxiosRequestConfig {
  suppressErrorToast?: boolean;
  suppressUnauthorizedRedirect?: boolean;
}

// --- Unified error classification ---

export type ErrorKind =
  | 'unauthorized'
  | 'quota_exceeded'
  | 'rate_limited'
  | 'validation_error'
  | 'server_error'
  | 'network_error'
  | 'unknown';

export type ClassifiedError = {
  kind: ErrorKind;
  message: string;
  /** HTTP status code if available */
  status?: number;
  /** Seconds until the client may retry (429 only) */
  retryAfterSeconds?: number | null;
  /** Parsed quota payload (quota_exceeded only) */
  quotaPayload?: QuotaExceededPayload | null;
};

function getRetryAfterSeconds(error: unknown): number | null {
  return extractRetryAfterSeconds(error);
}

/**
 * Classify an API error into a structured type for unified routing.
 * This is the single point of truth for error categorisation —
 * unauthorized, quota_exceeded, rate_limited, validation_error,
 * server_error, network_error, or unknown.
 */
export function classifyApiError(error: unknown, fallback = 'Request failed'): ClassifiedError {
  if (error === null || error === undefined) {
    return { kind: 'unknown', message: fallback };
  }

  const record = typeof error === 'object' ? (error as Record<string, unknown>) : null;
  const response = record?.response as Record<string, unknown> | undefined;
  const status = typeof response?.status === 'number' ? response.status : undefined;
  const message = extractApiErrorMessage(error, fallback);

  // No response → network error
  if (!response || status === undefined) {
    return { kind: 'network_error', message, status };
  }

  // 401 Unauthorized
  if (status === 401) {
    return { kind: 'unauthorized', message, status: 401 };
  }

  // 429: distinguish quota vs generic rate limit
  if (status === 429) {
    const retryAfterSeconds = getRetryAfterSeconds(error);
    if (isQuotaExceededError(error)) {
      const quotaPayload = buildQuotaExceededPayload(error as AxiosError);
      return { kind: 'quota_exceeded', message, status: 429, retryAfterSeconds, quotaPayload };
    }
    return { kind: 'rate_limited', message, status: 429, retryAfterSeconds };
  }

  // 422 Validation
  if (status === 422) {
    return { kind: 'validation_error', message, status: 422 };
  }

  // 5xx Server
  if (status >= 500 && status < 600) {
    return { kind: 'server_error', message, status };
  }

  return { kind: 'unknown', message, status };
}

// --- Redirect scoping ---

export function shouldRedirectOnUnauthorized(pathname: string): boolean {
  if (PUBLIC_AUTH_ROUTES.has(pathname)) {
    return false;
  }
  return true;
}

// --- Toast cooldown ---

function shouldShowToast(message: string, cooldownMs: number): boolean {
  const key = message.trim();
  if (!key) return true;
  const now = Date.now();
  const last = lastToastAt.get(key) ?? 0;
  if (now - last < cooldownMs) {
    return false;
  }
  lastToastAt.set(key, now);
  return true;
}

// --- Unauthorized handler ---

export function handleUnauthorized() {
  setAccessToken(null);
  localStorage.removeItem('auth-storage');
  // EPAR-9: a 401 must not leave the previous user's chat/session/class-context
  // data behind on a shared browser. Clear the chat store's persisted slice
  // (zustand persist name 'chat-storage' in stores/chatStore.ts) too.
  localStorage.removeItem('chat-storage');
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (shouldRedirectOnUnauthorized(pathname)) {
    window.location.href = '/login';
  }
}

// --- Classified error routing ---

function routeClassifiedError(classified: ClassifiedError, suppressErrorToast: boolean): void {
  switch (classified.kind) {
    case 'unauthorized':
      // Redirect handled by interceptor; toast suppressed (redirect is the UX signal)
      break;
    case 'quota_exceeded':
      // Quota toast is handled by QuotaExceededToastBridge via custom event
      if (classified.quotaPayload) {
        emitQuotaExceededEvent(classified.quotaPayload);
      }
      break;
    case 'rate_limited': {
      const cooldownMs = classified.retryAfterSeconds
        ? Math.max(classified.retryAfterSeconds * 1000, RATE_LIMIT_TOAST_COOLDOWN_MS)
        : RATE_LIMIT_TOAST_COOLDOWN_MS;
      if (!suppressErrorToast && shouldShowToast(classified.message, cooldownMs)) {
        toast.error(classified.message);
      }
      break;
    }
    case 'validation_error':
    case 'server_error':
    case 'network_error':
    case 'unknown':
      if (!suppressErrorToast && shouldShowToast(classified.message, DEFAULT_TOAST_COOLDOWN_MS)) {
        toast.error(classified.message);
      }
      break;
  }
}

// --- ApiBase class ---

export class ApiBase {
  protected client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: API_DEFAULT_TIMEOUT_MS, // 2 minutes for long-running requests
      // H-5b: the gateway mirrors the JWT into an HttpOnly cookie; the
      // browser attaches it when credentials ride along. Bearer header
      // (below) remains primary — cookie is the XSS-resistant fallback.
      withCredentials: true,
    });

    // Request interceptor: always use the unified token reader so
    // fresh module loads and warm in-memory sessions behave identically.
    this.client.interceptors.request.use(
      (config) => {
        const accessToken = getAccessToken();
        if (accessToken) {
          config.headers = config.headers ?? {};
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor — unified error surface
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const requestConfig = error.config as ApiRequestConfig | undefined;
        const suppressUnauthorizedRedirect = Boolean(requestConfig?.suppressUnauthorizedRedirect);
        const suppressErrorToast = Boolean(requestConfig?.suppressErrorToast);

        // P4-2（BUG-15）：/auth/me 的 5xx/网络错误做有限次静默重试——
        // 瞬时 503 不再级联触发强制登出。401 不重试（必须走登出路径）。
        const AUTH_ME_SILENT_RETRY_LIMIT = 2;
        const isAuthMeRequest = String(requestConfig?.url || '').includes('/auth/me');
        const errorStatus = (error.response as { status?: number } | undefined)?.status;
        const retriableForAuthMe = errorStatus === undefined || (errorStatus >= 500 && errorStatus < 600);
        if (isAuthMeRequest && retriableForAuthMe && requestConfig) {
          const retryConfig = requestConfig as typeof requestConfig & { __authMeRetryCount?: number };
          const retryCount = Number(retryConfig.__authMeRetryCount || 0);
          if (retryCount < AUTH_ME_SILENT_RETRY_LIMIT) {
            retryConfig.__authMeRetryCount = retryCount + 1;
            await new Promise((resolve) => window.setTimeout(resolve, 300 * (retryCount + 1)));
            return this.client.request(requestConfig);
          }
        }

        const classified = classifyApiError(error);

        // 401: clear token + redirect (unless suppressed)
        if (classified.kind === 'unauthorized' && !suppressUnauthorizedRedirect) {
          handleUnauthorized();
        }

        // Route all classified errors through unified toast / event surface
        routeClassifiedError(classified, suppressErrorToast);

        return Promise.reject(error);
      }
    );
  }
}
